// hud-readout.ts — render the in-game HUD CREDITS + POWER readouts through the REAL
// kifntHud bitmap font (HTML/DOM UI shell plan, Phase 5, Form 5 faithful-art pass).
//
// The canvas CreditsControl.OnPaint / PowerControl.OnPaint (inputui.ts:551-568 /
// 657-709) draw both readouts in the kifntHud font, whose glyphs A–L are ICONS /
// frame-pieces, NOT letters:
//   CREDITS: 'ABC' (or 'LBC' when the credit symbol is flashed off) = the framed BOX
//     ('A' = left cap + the $/credits icon, 'B' = the number-area background, 'C' =
//     right cap), then the number drawn RIGHT-ALIGNED inside the 'AB' span at
//     left + extent('AB') - extent(number).
//   POWER: 'DEF' = the framed BOX ('D' left cap, 'E' meter background, 'F' right cap),
//     'G' = the power symbol (shown/flashed), the meter PIPS (each glyph 'I' green /
//     'J' yellow / 'K' red, overlapping by 2px) starting at left + extent('D'), and
//     the demand marker 'H' centered at its meter fraction.
//
// A CSS approximation cannot match these glyphs, so we reproduce OnPaint EXACTLY into
// a CpuDibBitmap at the font's native resolution and hand the RGBA buffer back; the
// browser step (title-art.ts titleArtToDataUrl — the same bitmap→PNG path the menu
// art uses) turns it into an <img> the HudView sizes to extent × the design scale.
//
// This module is the pure render math (no DOM): node-testable. The OnPaint geometry
// is reproduced 1:1 — the only change is drawing at the bitmap origin (0,0) instead of
// the control's screen rect, since the produced bitmap IS the control.

import { CpuDibBitmap } from '../../gfx/dibbitmap.ts';
import type { DibBitmapLike } from '../../gfx/texatlasmgr.ts';
import { kPowerMeterPips, kCreditsDisplayMax } from './hud-format.ts';

/** The subset of the kifntHud Font the readout renderers use (font.ts) — a structural
 *  interface so the renderers are node-testable with a stub font (no atlas/pack). The
 *  real Font satisfies it; the canvas controls call the same four methods. */
export interface HudFontLike {
  GetHeight(): number;
  GetTextExtent(psz: string, cch?: number): number;
  DrawTextMultiline(
    pbm: DibBitmapLike,
    psz: string,
    x: number,
    y: number,
    cx: number,
    cy: number,
    fEllipsis: boolean,
  ): void;
  DrawText(pbm: DibBitmapLike, psz: string, x: number, y: number, cch?: number): number;
}

export interface ReadoutImage {
  /** the bitmap width in font (native) px — the credits/power extent('ABC'/'DEF'). */
  cx: number;
  /** the bitmap height in font (native) px — the font height. */
  cy: number;
  /** RGBA8 (ImageData byte order) — the same layout title-art.ts encodes to PNG. */
  rgba: Uint8Array;
  /** credits only: extent('AB') in native px — the RIGHT-align boundary for the credit
   *  number (the canvas draws the number ending here, before the 'C' right cap). The
   *  HUD overlays a WEB-font number right-aligned to this when drawNumber is false. */
  numRightPx?: number;
}

/** Reproduce CreditsControl.OnPaint (inputui.ts:551-568) into a bitmap.
 *  @param credits      plrLocal.GetCredits() — clamped to 99999 for display.
 *  @param drawSymbol   m_fDrawCreditSymbol — true ⇒ the $ icon shown ('ABC'),
 *                      false ⇒ the $ flashed off (the box stays the SAME size; only the
 *                      $ blinks — see below).
 *  @param drawNumber   true (canvas-faithful) ⇒ draw the credit number in the kifntHud
 *                      font too; false ⇒ render the BOX + $ icon ONLY (the HUD overlays
 *                      a WEB-font number instead, per the design preference). numRightPx
 *                      (extent('AB')) is returned either way for the overlay's right edge.
 *  The bitmap is extent('ABC') × font height — the canvas control's own size. */
export function renderCredits(
  pfnt: HudFontLike,
  credits: number,
  drawSymbol: boolean,
  drawNumber = true,
): ReadoutImage {
  // inputui.ts:544-545 — the control rect is extent('ABC') × font height.
  const cx = pfnt.GetTextExtent('ABC');
  const cy = pfnt.GetHeight();
  const pbm = new CpuDibBitmap(Math.max(1, cx), Math.max(1, cy));
  const numRightPx = pfnt.GetTextExtent('AB');

  // The framed box. NORMAL: 'ABC' (left cap WITH the $ icon, 'B' number-area fill, 'C'
  // right cap). FLASH-OFF: the canvas draws 'LBC', but the 'L' left cap is NARROWER than
  // 'A' (it lacks the $ icon's horizontal span), so 'LBC' is extent('A')-extent('L') px
  // narrower than 'ABC' — drawn left-aligned the 'C' right cap (and the whole frame)
  // SHRINKS leftward, leaving the right-aligned credit number (pinned at extent('AB'))
  // hanging OUTSIDE the box. To keep the box STABLE while flashing (only the $ blinks,
  // the frame never moves), we reproduce 'ABC' WITHOUT the $: fill the full interior with
  // the 'B' number-area glyph (an opaque, uniform horizontal fill — identical borders/
  // interior to 'A', just no $) and pin the 'C' right cap to the SAME x as in 'ABC'. The
  // 'B' tiles overlap (idempotent opaque fill ⇒ no seam) so the frame is continuous and
  // full-width; the number boundary (extent('AB')) is unchanged across both phases.
  if (drawSymbol) {
    pfnt.DrawTextMultiline(pbm, 'ABC', 0, 0, cx, -1, false);
  } else {
    // FLASH-OFF: 'A' bundles the box's rounded LEFT CAP *and* the $ symbol, and the dedicated
    // flash-off cap glyph 'L' is broken in the 832 pack (4px of pure transparent space), so
    // dropping 'A' left the box with NO left side ("the left-hand side is gone"). Rebuild a
    // complete frame WITHOUT the $: fill the interior with 'B', pin 'C' at the right edge,
    // and synthesize the left cap by MIRRORING the 'C' right cap into x=0. Only the $ blinks;
    // the frame stays complete + the same width.
    const cxB = pfnt.GetTextExtent('B');
    const cxC = pfnt.GetTextExtent('C');
    const xC = cx - cxC; // the 'C' right cap pinned to the 'ABC' right edge
    pfnt.DrawTextMultiline(pbm, 'B', cxC, 0, cx, -1, false); // interior, after the left cap
    pfnt.DrawTextMultiline(pbm, 'B', xC - cxB, 0, cx, -1, false); // fill up to 'C' (overlap)
    pfnt.DrawTextMultiline(pbm, 'C', xC, 0, cx, -1, false); // right cap, fixed x
    // the left cap = the 'C' right cap mirrored horizontally into x=0..cxC (drawn LAST so it
    // sits on top of the 'B' fill, exactly like 'C' caps the right).
    const capBm = new CpuDibBitmap(Math.max(1, cxC), Math.max(1, cy));
    pfnt.DrawTextMultiline(capBm, 'C', 0, 0, cxC, -1, false);
    for (let row = 0; row < cy; row++) {
      for (let col = 0; col < cxC; col++) {
        const sp = (row * cxC + col) * 4;
        const dp = (row * cx + (cxC - 1 - col)) * 4;
        pbm.rgba[dp] = capBm.rgba[sp];
        pbm.rgba[dp + 1] = capBm.rgba[sp + 1];
        pbm.rgba[dp + 2] = capBm.rgba[sp + 2];
        pbm.rgba[dp + 3] = capBm.rgba[sp + 3];
      }
    }
  }

  // inputui.ts:564-567 — the number, RIGHT-ALIGNED inside the 'AB' span. Skipped when
  // the HUD draws the number in the web font instead (drawNumber=false).
  if (drawNumber) {
    const szT = String(credits > kCreditsDisplayMax ? kCreditsDisplayMax : credits);
    pfnt.DrawTextMultiline(pbm, szT, numRightPx - pfnt.GetTextExtent(szT), 0, cx, -1, false);
  }

  return { cx: pbm.cx, cy: pbm.cy, rgba: pbm.rgba, numRightPx };
}

/** Reproduce PowerControl.OnPaint (inputui.ts:657-709) into a bitmap.
 *  @param demand        plrLocal.GetPowerDemand().
 *  @param supply        plrLocal.GetPowerSupply().
 *  @param supplyPerPip  apuntc[kutReactor].nPowerSupply — units per pip (inputui.ts:692).
 *  @param demandMax     PowerControl.m_nDemandMax — the yellow-zone threshold.
 *  @param showSymbol    m_fShowPowerSymbol — true ⇒ the 'G' power symbol is drawn.
 *
 *  CANVAS-CLIP PARITY: the canvas control rect is extent('DEF') × font height
 *  (inputui.ts:635-636), but OnPaint draws into the WHOLE InputUIForm DIB at the
 *  control's offset, so any glyph that runs PAST extent('DEF') (a high-supply meter,
 *  or the demand marker near the meter's right end) spills RIGHT into the bar — the
 *  canvas does NOT clip it to the control rect. A tight extent('DEF') bitmap WOULD
 *  clip that spill (CpuDibBitmap.blitCore drops writes past `cx`), so the DOM readout
 *  diverged from the canvas (content cut off). We therefore size the bitmap to the FULL
 *  drawn extent — the box stays left-anchored at x=0 (its layout position is unchanged;
 *  the HUD left-anchors the <img>), and the extra width extends RIGHT exactly as the
 *  canvas spills. numRightPx carries extent('DEF') (the FRAME width) so the HUD can keep
 *  the box-relative layout stable while the image itself may be wider.
 *  NOTE: the kifntHud font's yellow-pip glyph 'J' is an 18px-wide block (vs the 4px
 *  green 'I'); 'K' is absent. So a YELLOW/over-supply meter draws wide overlapping
 *  blocks — that look is the shipped FONT DATA (data/832/hudfont.fnt), identical on the
 *  canvas; we reproduce it faithfully (no glyph substitution) but no longer CLIP it. */
export function renderPower(
  pfnt: HudFontLike,
  demand: number,
  supply: number,
  supplyPerPip: number,
  demandMax: number,
  showSymbol: boolean,
): ReadoutImage {
  // inputui.ts:635-636 — the control rect (and the box frame) is extent('DEF') wide.
  const cxFrame = pfnt.GetTextExtent('DEF');
  const cy = pfnt.GetHeight();

  // inputui.ts:672-688 — the meter geometry + the 3-state pip glyph (measured up front
  // so we can size the bitmap to the full drawn extent before allocating).
  const cxLeftSide = pfnt.GetTextExtent('D');
  const cxMeter = pfnt.GetTextExtent('E');
  // The canvas picks the pip glyph by power state: 'I' green / 'J' yellow (supply-demand
  // <= demandMax) / 'K' red (supply < demand). BUT in the default 8bpp pack (data/832/
  // hudfont.fnt) only the green 'I' is a correct 4px pip — 'J' is a broken 18px solid BLOCK
  // and 'K' is absent, so a yellow/low meter rendered a wide overflowing smear (10 blocks →
  // ~180px, "the power indicator screwed up"). Use the green 'I' pip for ALL states so the
  // meter is a clean uniform 4px-pip bar; the low-power warning is the flashing 'G' lightning
  // (faithful + unaffected). (The ?2432 true-color pack ships correct colored pips.)
  const cxPip = pfnt.GetTextExtent('I');
  const pszTick = 'I';

  // inputui.ts:691-699 — the supply pips (capped at kcPowerMeterPips; laid on cxStride, below).
  const nSupplyPerPip = supplyPerPip;
  let cSupplyPips = nSupplyPerPip > 0 ? Math.trunc(supply / nSupplyPerPip) : 0;
  if (cSupplyPips > kPowerMeterPips) cSupplyPips = kPowerMeterPips;
  if (cSupplyPips < 0) cSupplyPips = 0;

  // The pip STRIDE. inputui.ts:697-698 overlaps the pips by 2px — a hack tuned for the
  // ?2432 glyphs (8px pip, 62px meter: a 6px stride lays 10 pips exactly across the meter).
  // In the default 824/832 packs (4px pip, 40px meter) that 2px stride packed all 10 pips
  // into the left 22px while the demand marker below is scaled to the full 40px, so the
  // marker sat PAST the last pip whenever demand > ~supply/2 + 20 — the meter read "demand
  // over production" with no low-power effect in the sim (Reddit report, 2026-09). Lay the
  // pips so 10 span the meter (cxMeter - cxPip over 9 gaps): 4px for 824/832, unchanged 6px
  // for 2432; never tighter than the original overlap.
  const cxStride = Math.max(
    cxPip - 2,
    Math.min(cxPip, Math.trunc((cxMeter - cxPip) / (kPowerMeterPips - 1))),
  );

  // inputui.ts:701-708 — the demand indicator 'H', centered at its meter fraction.
  const cxDemand = pfnt.GetTextExtent('H');
  // The meter's full scale is 10 pips' worth of supply — or, past the 10-pip cap (11+
  // Generators), the actual supply: the original kept scaling to 10 pips, so the marker
  // ran off the end of a full meter while power was still in surplus.
  const nMeterScale = Math.max(nSupplyPerPip * kPowerMeterPips, supply);
  const xDemand = nSupplyPerPip > 0 ? Math.trunc((demand * cxMeter) / nMeterScale) : 0;
  const xDemandDraw = cxLeftSide + xDemand - Math.trunc(cxDemand / 2);

  // the FULL drawn extent (the canvas reaches all of these in the form DIB): the frame,
  // the last pip's right edge (each pip i sits at cxLeftSide + i*cxStride), and the
  // demand marker's right edge. Size the bitmap to fit it all (no right clip).
  let cxContent = cxFrame;
  if (cSupplyPips > 0) {
    const lastPipX = cxLeftSide + (cSupplyPips - 1) * cxStride;
    cxContent = Math.max(cxContent, lastPipX + cxPip);
  }
  cxContent = Math.max(cxContent, xDemandDraw + cxDemand);
  const pbm = new CpuDibBitmap(Math.max(1, cxContent), Math.max(1, cy));

  // inputui.ts:662 — the framed box (left-anchored at 0, unchanged layout position).
  pfnt.DrawTextMultiline(pbm, 'DEF', 0, 0, cxFrame, -1, false);

  // inputui.ts:666-668 — the power symbol (flashed via showSymbol).
  if (showSymbol) {
    pfnt.DrawTextMultiline(pbm, 'G', 0, 0, cxFrame, -1, false);
  }

  for (let i = 0; i < cSupplyPips; i++) {
    pfnt.DrawText(pbm, pszTick, cxLeftSide + i * cxStride, 0, 1);
  }

  pfnt.DrawTextMultiline(pbm, 'H', xDemandDraw, 0, cxDemand, -1, false);

  // numRightPx = the FRAME width (extent('DEF')) — the box-relative right edge the HUD
  // anchors layout to, kept stable even when the bitmap is wider (canvas spill).
  return { cx: pbm.cx, cy: pbm.cy, rgba: pbm.rgba, numRightPx: cxFrame };
}
